import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useSubscription } from '../App'
import { ROMANS_1_DATA, ChartData, Arc } from '../data/romans1'
import { BIBLE_BOOKS } from '../data/bibleBooks'
import ChapterChart from './ChapterChart'

// ── Types ────────────────────────────────────────────────────────────────────

interface KeyTerm {
  term: string
  definition: string
  verses: number[]
}

interface ParagraphData {
  id: string
  number: number
  title: string
  startVerse: number
  endVerse: number
  topic: string
  summary: string
  keyPoints: string[]
  interpretiveNotes: string
  verses: { number: number; text: string }[]
}

interface ChartState {
  id: string | null
  book: string
  chapter: number
  title: string
  classification: string
  unity: string
  problem: string
  keyTerms: KeyTerm[]
  paragraphs: ParagraphData[]
  interpretiveNotes: string
  isPublic: boolean
  isCodexGenerated: boolean
  codexProfile: string
  codexVoice: string
  arcs: Arc[]
}

const THEOLOGIAN_PROFILES = [
  'Martin Luther',
  'Phillip Melanchthon',
  'Oswald Bayer',
  'Erick Sorensen',
  'Dan Price',
  'C.F.W. Walther',
  'Hermann Sasse',
]

const EMPTY_CHART: ChartState = {
  id: null,
  book: 'Romans',
  chapter: 1,
  title: '',
  classification: '',
  unity: '',
  problem: '',
  keyTerms: [],
  paragraphs: [],
  interpretiveNotes: '',
  isPublic: false,
  isCodexGenerated: false,
  codexProfile: 'Oswald Bayer',
  codexVoice: 'Dan Price',
  arcs: [],
}

// ── Editor Component ─────────────────────────────────────────────────────────

export default function Editor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isSubscriber = useSubscription()
  const isNew = id === undefined || id === 'new'

  const [chart, setChart] = useState<ChartState>(EMPTY_CHART)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [fetchingVerses, setFetchingVerses] = useState(false)
  const [saved, setSaved] = useState(false)
  const [bookInput, setBookInput] = useState('Romans')
  const [chapterInput, setChapterInput] = useState('1')
  const [activeTab, setActiveTab] = useState<'chart' | 'terms' | 'arcs' | 'notes'>('chart')
  const [showPrint, setShowPrint] = useState(false)
  const [chartMode, setChartMode] = useState<'blank' | 'codex'>('blank')
  const [codexProfile, setCodexProfile] = useState('Oswald Bayer')
  const [codexVoice, setCodexVoice] = useState('Dan Price')
  const [spinFrame, setSpinFrame] = useState(0)
  const spinRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (fetchingVerses && chartMode === 'codex') {
      spinRef.current = setInterval(() => setSpinFrame(f => (f + 1) % 8), 120)
    } else {
      if (spinRef.current) clearInterval(spinRef.current)
      setSpinFrame(0)
    }
    return () => { if (spinRef.current) clearInterval(spinRef.current) }
  }, [fetchingVerses, chartMode])

  // Map editor state → ChartData for the ChapterChart component
  const chartDataForPrint = (): ChartData => ({
    book: chart.book,
    chapter: chart.chapter,
    classification: chart.classification,
    unity: chart.unity,
    problem: chart.problem,
    keyTerms: chart.keyTerms,
    paragraphs: chart.paragraphs,
    interpretiveNotes: chart.interpretiveNotes,
    arcs: chart.arcs.length > 0 ? chart.arcs : undefined,
  })

  // Load existing chart, or reset to blank for new charts.
  // This also handles React Router reusing the component instance when navigating
  // between chart routes (e.g. /chart/abc → /chart/new) without a full unmount.
  useEffect(() => {
    if (!isNew && id) {
      setChart(EMPTY_CHART)
      loadChart(id)
    } else {
      setChart(EMPTY_CHART)
      setBookInput('Romans')
      setChapterInput('1')
      setLoading(false)
    }
  }, [id])

  const loadChart = async (chartId: string) => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('charts')
        .select('*, chapters(book, chapter_num)')
        .eq('id', chartId)
        .single()

      if (error) throw error

      setChart({
        id: data.id,
        book: (data as any).chapters?.book || '',
        chapter: (data as any).chapters?.chapter_num || 1,
        title: data.title || '',
        classification: data.classification || '',
        unity: data.unity || '',
        problem: data.problem || '',
        keyTerms: data.key_terms || [],
        paragraphs: data.paragraphs || [],
        interpretiveNotes: data.interpretive_notes || '',
        isPublic: data.is_public || false,
        isCodexGenerated: data.is_codex_generated || false,
        codexProfile: data.codex_profile || 'Oswald Bayer',
        codexVoice: data.codex_voice || 'Dan Price',
        arcs: data.arcs || [],
      })
    } catch (err) {
      console.error('Error loading chart:', err)
    } finally {
      setLoading(false)
    }
  }

  // Pre-fill with Romans 1 example data
  const loadExample = () => {
    const d = ROMANS_1_DATA
    setChart(prev => ({
      ...prev,
      book: d.book,
      chapter: d.chapter,
      title: `${d.book} ${d.chapter}`,
      classification: d.classification,
      unity: d.unity,
      problem: d.problem,
      keyTerms: d.keyTerms,
      paragraphs: d.paragraphs.map(p => ({
        id: p.id,
        number: p.number,
        title: p.title,
        startVerse: p.startVerse,
        endVerse: p.endVerse,
        topic: p.topic,
        summary: p.summary,
        keyPoints: p.keyPoints,
        interpretiveNotes: p.interpretiveNotes,
        verses: p.verses,
      })),
      interpretiveNotes: d.interpretiveNotes,
    }))
    setBookInput(d.book)
    setChapterInput(String(d.chapter))
  }

  // Parse a chunk of ESV text into verse objects.
  // The ESV API returns passages as a plain-text string with verse numbers
  // in brackets: "[1] Paul, a servant… [2] the gospel he promised…"
  const parseESVVerses = (text: string): { number: number; text: string }[] => {
    const parts = text.split(/\[(\d+)\]/)
    const verses: { number: number; text: string }[] = []
    for (let i = 1; i < parts.length; i += 2) {
      const num = parseInt(parts[i], 10)
      const raw = (parts[i + 1] || '').replace(/\s+/g, ' ').trim()
      const clean = raw.replace(/\s*\(ESV\)\s*$/, '').trim()
      if (num && clean) verses.push({ number: num, text: clean })
    }
    return verses
  }

  // Split the full ESV passage text into paragraph blocks using natural
  // paragraph breaks (double newlines). Each block becomes one ParagraphData.
  const parseESVParagraphs = (text: string): ParagraphData[] => {
    // Split on two or more newlines (paragraph breaks in ESV plain-text output)
    const chunks = text.split(/\n{2,}/).map(c => c.trim()).filter(c => c.length > 0)
    const paragraphs: ParagraphData[] = []

    for (const chunk of chunks) {
      const verses = parseESVVerses(chunk)
      if (verses.length === 0) continue  // skip section-heading-only chunks

      const startVerse = verses[0].number
      const endVerse = verses[verses.length - 1].number

      paragraphs.push({
        id: `para-${startVerse}`,
        number: paragraphs.length + 1,
        title: '',
        startVerse,
        endVerse,
        topic: '',
        summary: '',
        keyPoints: [],
        interpretiveNotes: '',
        verses,
      })
    }

    // Fallback: if no paragraph breaks found, put everything in one block
    if (paragraphs.length === 0) {
      const allVerses = parseESVVerses(text)
      if (allVerses.length > 0) {
        paragraphs.push({
          id: 'para-1',
          number: 1,
          title: '',
          startVerse: allVerses[0].number,
          endVerse: allVerses[allVerses.length - 1].number,
          topic: '',
          summary: '',
          keyPoints: [],
          interpretiveNotes: '',
          verses: allVerses,
        })
      }
    }

    return paragraphs
  }

  // Codex Fill — call generate-chart edge function, get fully analysed chart back
  const generateCodexChart = async (profile: string, voice: string) => {
    setFetchingVerses(true)
    try {
      const { data, error } = await supabase.functions.invoke('generate-chart', {
        body: {
          book: bookInput.trim(),
          chapter: parseInt(chapterInput),
          theologianProfile: profile,
          voiceProfile: voice,
        }
      })
      if (error) throw error
      if (data.error) throw new Error(data.error)

      setChart(prev => ({
        ...prev,
        book: bookInput.trim(),
        chapter: parseInt(chapterInput),
        title: `${bookInput.trim()} ${chapterInput}`,
        classification: data.classification || '',
        unity: data.unity || '',
        problem: data.problem || '',
        interpretiveNotes: data.interpretiveNotes || '',
        paragraphs: data.paragraphs || [],
        arcs: data.arcs || [],
        isCodexGenerated: true,
        codexProfile: profile,
        codexVoice: voice,
      }))
    } catch (err: any) {
      alert('Codex generation failed: ' + (err.message || 'Unknown error'))
    } finally {
      setFetchingVerses(false)
    }
  }

  // Fetch verses from ESV Edge Function
  const fetchVerses = async (codexOpts?: { profile: string; voice: string }) => {
    setFetchingVerses(true)
    try {
      const { data, error } = await supabase.functions.invoke('fetch-chapter', {
        body: { book: bookInput.trim(), chapter: parseInt(chapterInput) }
      })

      if (error) throw error
      if (!data) throw new Error('No data returned from server')

      // Support two response shapes:
      //   1. Edge function already processed: { verses: [{number, text}], versesCount }
      //   2. Raw ESV API passthrough: { passages: ["[1] text [2] text …"] }
      let paragraphs: ParagraphData[] = []

      if (Array.isArray(data.verses) && data.verses.length > 0) {
        // Pre-processed: one paragraph per verse, user can split/merge
        paragraphs = (data.verses as { number: number; text: string }[]).map((verse, idx) => ({
          id: `para-${verse.number}`,
          number: idx + 1,
          title: '',
          startVerse: verse.number,
          endVerse: verse.number,
          topic: '',
          summary: '',
          keyPoints: [],
          interpretiveNotes: '',
          verses: [verse],
        }))
      } else if (Array.isArray(data.passages) && data.passages.length > 0) {
        // Raw ESV passthrough — parse paragraph breaks from the text
        paragraphs = parseESVParagraphs(data.passages.join('\n\n'))
      } else {
        throw new Error(
          'Unexpected response format. Make sure the fetch-chapter edge function is deployed and the ESV_API_KEY is set.'
        )
      }

      if (paragraphs.length === 0) {
        throw new Error(`No verses found for ${bookInput.trim()} ${chapterInput}. Check the book name spelling.`)
      }

      setChart(prev => ({
        ...prev,
        book: bookInput.trim(),
        chapter: parseInt(chapterInput),
        title: `${bookInput.trim()} ${chapterInput}`,
        paragraphs,
        ...(codexOpts ? {
          isCodexGenerated: true,
          codexProfile: codexOpts.profile,
          codexVoice: codexOpts.voice,
        } : {
          isCodexGenerated: false,
        }),
      }))
    } catch (err: any) {
      alert('Could not load verses: ' + (err.message || 'Unknown error'))
    } finally {
      setFetchingVerses(false)
    }
  }

  // Split a paragraph at a verse boundary
  const splitParagraph = (paraId: string, atVerse: number) => {
    setChart(prev => {
      const idx = prev.paragraphs.findIndex(p => p.id === paraId)
      if (idx === -1) return prev

      const para = prev.paragraphs[idx]
      if (atVerse <= para.startVerse || atVerse > para.endVerse) return prev

      const verses1 = para.verses.filter(v => v.number < atVerse)
      const verses2 = para.verses.filter(v => v.number >= atVerse)

      const newParas = [...prev.paragraphs]
      newParas.splice(idx, 1,
        {
          ...para,
          endVerse: atVerse - 1,
          verses: verses1,
          title: `Paragraph ${idx + 1}`,
          id: `para-${Date.now()}-a`,
        },
        {
          id: `para-${Date.now()}-b`,
          number: idx + 2,
          title: `Paragraph ${idx + 2}`,
          startVerse: atVerse,
          endVerse: para.endVerse,
          topic: '',
          summary: '',
          keyPoints: [],
          interpretiveNotes: '',
          verses: verses2,
        }
      )

      // Renumber
      return {
        ...prev,
        paragraphs: newParas.map((p, i) => ({ ...p, number: i + 1 }))
      }
    })
  }

  // Save chart to Supabase
  const saveChart = async () => {
    setSaving(true)
    try {
      // 1. Verify the user session is still alive
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) throw new Error(`Auth error: ${userError.message}`)
      if (!user) throw new Error('Session expired — please refresh and log in again.')

      // 2. Look up (or create) the chapter record
      const { data: existingChapter, error: selectError } = await supabase
        .from('chapters')
        .select('id')
        .eq('book', chart.book)
        .eq('chapter_num', chart.chapter)
        .maybeSingle()   // returns null (not error) when no row found

      if (selectError) throw new Error(`Chapter lookup failed: ${selectError.message}`)

      let chapterId: string | null = existingChapter?.id ?? null

      if (!chapterId) {
        const { data: newChapter, error: chapterInsertError } = await supabase
          .from('chapters')
          .insert({ book: chart.book, chapter_num: chart.chapter, verses_count: 0 })
          .select('id')
          .single()
        if (chapterInsertError) throw new Error(`Chapter insert failed: ${chapterInsertError.message}`)
        if (!newChapter) throw new Error('Chapter insert returned no data.')
        chapterId = newChapter.id
      }

      // 3. Build and save the chart payload
      const payload = {
        user_id: user.id,
        chapter_id: chapterId,
        title: chart.title || `${chart.book} ${chart.chapter}`,
        classification: chart.classification,
        unity: chart.unity,
        problem: chart.problem,
        key_terms: chart.keyTerms,
        paragraphs: chart.paragraphs,
        interpretive_notes: chart.interpretiveNotes,
        is_public: chart.isPublic,
        is_codex_generated: chart.isCodexGenerated,
        codex_profile: chart.codexProfile,
        codex_voice: chart.codexVoice,
        arcs: chart.arcs,
        updated_at: new Date().toISOString(),
      }

      if (chart.id) {
        const { error: updateError } = await supabase.from('charts').update(payload).eq('id', chart.id)
        if (updateError) throw new Error(`Chart update failed: ${updateError.message}`)
      } else {
        const { data: newChart, error: insertError } = await supabase
          .from('charts').insert(payload).select('id').single()
        if (insertError) throw new Error(`Chart insert failed: ${insertError.message}`)
        if (!newChart) throw new Error('Chart insert returned no data.')
        setChart(prev => ({ ...prev, id: newChart.id }))
        navigate(`/chart/${newChart.id}`, { replace: true })
      }

      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err: any) {
      alert('Save failed: ' + (err?.message ?? String(err)))
    } finally {
      setSaving(false)
    }
  }

  // Update a paragraph field
  const updatePara = (paraId: string, field: keyof ParagraphData, value: any) => {
    setChart(prev => ({
      ...prev,
      paragraphs: prev.paragraphs.map(p => p.id === paraId ? { ...p, [field]: value } : p)
    }))
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <p>Loading chart...</p>
    </div>
  )

  return (
    <div style={styles.page}>
      {/* Top Nav */}
      <nav style={styles.nav}>
        <button style={styles.backBtn} onClick={() => navigate('/')}>← Dashboard</button>
        <span style={styles.navTitle}>
          {chart.book} {chart.chapter} {chart.title ? `— ${chart.title}` : ''}
        </span>
        <div style={styles.navRight}>
          {isSubscriber ? (
            <label style={styles.publicLabel}>
              <input
                type="checkbox"
                checked={chart.isPublic}
                onChange={e => setChart(prev => ({ ...prev, isPublic: e.target.checked }))}
              />
              {' '}Public
            </label>
          ) : (
            <a
              href="https://disputation.app/#settings"
              target="_blank"
              rel="noopener"
              style={{ ...styles.publicLabel, opacity: 0.6, textDecoration: 'none', fontSize: '13px' }}
              title="Sharing requires a Pro subscription"
            >
              🔒 Share
            </a>
          )}
          <button style={styles.printPreviewBtn} onClick={() => setShowPrint(true)} disabled={chart.paragraphs.length === 0}>
            🖨 Print Preview
          </button>
          <button style={styles.saveBtn} onClick={saveChart} disabled={saving}>
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save'}
          </button>
        </div>
      </nav>

      {/* Chapter Loader (only for new charts) */}
      {isNew && chart.paragraphs.length === 0 && (
        <div style={styles.loaderWrap}>
          {/* Row 1: Book + Chapter + Mode toggle */}
          <div style={styles.loaderBar}>
            <select
              style={styles.loaderSelect}
              value={bookInput}
              onChange={e => { setBookInput(e.target.value); setChapterInput('1') }}
            >
              <optgroup label="Old Testament">
                {BIBLE_BOOKS.filter(b => b.testament === 'OT').map(b => (
                  <option key={b.name} value={b.name}>{b.name}</option>
                ))}
              </optgroup>
              <optgroup label="New Testament">
                {BIBLE_BOOKS.filter(b => b.testament === 'NT').map(b => (
                  <option key={b.name} value={b.name}>{b.name}</option>
                ))}
              </optgroup>
            </select>

            <select
              style={{ ...styles.loaderSelect, width: '90px' }}
              value={chapterInput}
              onChange={e => setChapterInput(e.target.value)}
            >
              {Array.from(
                { length: BIBLE_BOOKS.find(b => b.name === bookInput)?.chapters ?? 1 },
                (_, i) => i + 1
              ).map(n => (
                <option key={n} value={n}>Ch. {n}</option>
              ))}
            </select>

            <div style={styles.modeToggle}>
              <button
                style={{ ...styles.modeBtn, ...(chartMode === 'blank' ? styles.modeBtnActive : {}) }}
                onClick={() => setChartMode('blank')}
              >
                Blank Chart
              </button>
              {isSubscriber ? (
                <button
                  style={{ ...styles.modeBtn, ...(chartMode === 'codex' ? styles.modeBtnActive : {}) }}
                  onClick={() => setChartMode('codex')}
                >
                  Codex Fill
                </button>
              ) : (
                <a
                  href="https://disputation.app/#settings"
                  target="_blank"
                  rel="noopener"
                  style={{ ...styles.modeBtn, textDecoration: 'none', opacity: 0.7, cursor: 'pointer' }}
                  title="Codex Fill requires a Pro subscription"
                >
                  🔒 Codex Fill
                </a>
              )}
            </div>

            <button
              style={styles.loadBtn}
              onClick={() => chartMode === 'codex'
                ? generateCodexChart(codexProfile, codexVoice)
                : fetchVerses()
              }
              disabled={fetchingVerses}
            >
              {fetchingVerses
                ? (chartMode === 'codex' ? 'Generating…' : 'Loading…')
                : chartMode === 'codex' ? 'Load & Generate' : 'Load from ESV'
              }
            </button>
            <span style={{ color: '#93c5fd', fontSize: '13px' }}>or</span>
            <button style={styles.exampleBtn} onClick={loadExample}>
              Use Romans 1 Example
            </button>
          </div>

          {/* Row 2: Codex profile selectors (only when Codex Fill active) */}
          {chartMode === 'codex' && (
            <div style={styles.codexBar}>
              <span style={styles.codexBarLabel}>Codex options:</span>
              <div style={styles.codexField}>
                <label style={styles.codexFieldLabel}>Theological Profile</label>
                <select
                  style={styles.codexSelect}
                  value={codexProfile}
                  onChange={e => setCodexProfile(e.target.value)}
                >
                  {THEOLOGIAN_PROFILES.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div style={styles.codexField}>
                <label style={styles.codexFieldLabel}>Voice</label>
                <select
                  style={styles.codexSelect}
                  value={codexVoice}
                  onChange={e => setCodexVoice(e.target.value)}
                >
                  {THEOLOGIAN_PROFILES.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Codex generation spinner */}
      {fetchingVerses && chartMode === 'codex' && (
        <div style={styles.spinnerWrap}>
          <div style={styles.spinnerCard}>
            <div style={styles.spinnerRing}>
              {['▐','▀','▌','▄','▐','▀','▌','▄'][spinFrame]}
            </div>
            <div style={styles.spinnerText}>Generating chart…</div>
            <div style={styles.spinnerSub}>
              {codexProfile} profile · {codexVoice} voice
            </div>
            <div style={styles.spinnerHint}>This takes 15–30 seconds</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        {(['chart', 'terms', 'arcs', 'notes'] as const).map(tab => (
          <button
            key={tab}
            style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'chart' ? 'Chart'
              : tab === 'terms' ? 'Key Terms'
              : tab === 'arcs' ? `Arcs${chart.arcs.length > 0 ? ` (${chart.arcs.length})` : ''}`
              : 'Interpretive Notes'}
          </button>
        ))}
      </div>

      {/* Codex metadata banner */}
      {chart.isCodexGenerated && (
        <div style={styles.codexBanner}>
          <span style={styles.codexBannerTitle}>Codex Generation</span>
          <div style={styles.codexBannerItems}>
            <span style={styles.codexBannerItem}>
              <span style={styles.codexCheck}>✓</span> Core Theological Assumptions
            </span>
            <span style={styles.codexBannerItem}>
              <span style={styles.codexCheck}>✓</span> {chart.codexProfile} Emphasis
            </span>
            <span style={styles.codexBannerItem}>
              <span style={styles.codexCheck}>✓</span> {chart.codexVoice} Voice
            </span>
          </div>
        </div>
      )}

      {/* ── TAB: CHART ── */}
      {activeTab === 'chart' && (
        <div style={styles.content}>
          {/* Header Section */}
          <div style={styles.headerSection}>
            <div style={styles.headerField}>
              <label style={styles.fieldLabel}>Classification</label>
              <small style={styles.fieldHint}>What kind of writing is this?</small>
              <textarea
                style={styles.textarea}
                value={chart.classification}
                onChange={e => setChart(prev => ({ ...prev, classification: e.target.value }))}
                placeholder="e.g. Epistle (formal theological letter)"
                rows={2}
              />
            </div>
            <div style={styles.headerField}>
              <label style={styles.fieldLabel}>Unity</label>
              <small style={styles.fieldHint}>The whole chapter in one sentence</small>
              <textarea
                style={styles.textarea}
                value={chart.unity}
                onChange={e => setChart(prev => ({ ...prev, unity: e.target.value }))}
                placeholder="e.g. Paul establishes that righteousness comes through faith alone..."
                rows={2}
              />
            </div>
            <div style={styles.headerField}>
              <label style={styles.fieldLabel}>Problem</label>
              <small style={styles.fieldHint}>What is the author solving?</small>
              <textarea
                style={styles.textarea}
                value={chart.problem}
                onChange={e => setChart(prev => ({ ...prev, problem: e.target.value }))}
                placeholder="e.g. To explain the gospel and his apostolic authority..."
                rows={2}
              />
            </div>
          </div>

          {/* Paragraphs */}
          {(chart.paragraphs || []).length === 0 ? (
            <div style={styles.noParagraphs}>
              <p>Load a chapter above to begin your analysis.</p>
            </div>
          ) : (
            <div style={styles.paragraphList}>
              {(chart.paragraphs || []).map((para, idx) => {
                const verseCount = para.endVerse - para.startVerse + 1
                const totalVerses = chart.paragraphs.reduce(
                  (sum, p) => sum + (p.endVerse - p.startVerse + 1), 0
                )
                const heightPct = (verseCount / totalVerses) * 100

                return (
                  <div key={para.id} style={{ ...styles.paraCard, minHeight: `${Math.max(heightPct * 4, 80)}px` }}>
                    {/* Paragraph header */}
                    <div style={styles.paraHeader}>
                      <span style={styles.paraBadge}>¶ {para.number}</span>
                      <input
                        style={styles.paraTitleInput}
                        value={para.title}
                        onChange={e => updatePara(para.id, 'title', e.target.value)}
                        placeholder="Paragraph title..."
                      />
                      <span style={styles.paraVerses}>vv. {para.startVerse}–{para.endVerse}</span>

                      {/* Split at verse */}
                      {verseCount > 1 && (
                        <select
                          style={styles.splitSelect}
                          defaultValue=""
                          onChange={e => {
                            if (e.target.value) splitParagraph(para.id, parseInt(e.target.value))
                          }}
                        >
                          <option value="">Split at verse...</option>
                          {(para.verses || []).slice(1).map(v => (
                            <option key={v.number} value={v.number}>
                              v. {v.number}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Verses */}
                    <div style={styles.versesText}>
                      {(para.verses || []).map(v => (
                        <span key={v.number}>
                          <sup style={styles.verseNum}>{v.number}</sup>
                          {v.text}{' '}
                        </span>
                      ))}
                    </div>

                    {/* Analysis fields */}
                    <div style={styles.analysisGrid}>
                      <div>
                        <label style={styles.fieldLabel}>Topic</label>
                        <input
                          style={styles.input}
                          value={para.topic}
                          onChange={e => updatePara(para.id, 'topic', e.target.value)}
                          placeholder="Main topic..."
                        />
                      </div>
                      <div>
                        <label style={styles.fieldLabel}>Summary</label>
                        <textarea
                          style={styles.textarea}
                          value={para.summary}
                          onChange={e => updatePara(para.id, 'summary', e.target.value)}
                          placeholder="What does this paragraph say?"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: KEY TERMS ── */}
      {activeTab === 'terms' && (
        <div style={styles.content}>
          <div style={styles.termsHeader}>
            <h3 style={styles.tabSectionTitle}>Key Terms</h3>
            <button
              style={styles.addBtn}
              onClick={() => setChart(prev => ({
                ...prev,
                keyTerms: [...prev.keyTerms, { term: '', definition: '', verses: [] }]
              }))}
            >
              + Add Term
            </button>
          </div>

          {(chart.keyTerms || []).length === 0 ? (
            <p style={styles.emptyText}>No key terms yet. Click "Add Term" to begin.</p>
          ) : (
            <div style={styles.termsList}>
              {(chart.keyTerms || []).map((kt, idx) => (
                <div key={idx} style={styles.termCard}>
                  <div style={styles.termRow}>
                    <input
                      style={{ ...styles.input, fontWeight: '600', flex: 1 }}
                      value={kt.term}
                      onChange={e => {
                        const updated = [...chart.keyTerms]
                        updated[idx] = { ...updated[idx], term: e.target.value }
                        setChart(prev => ({ ...prev, keyTerms: updated }))
                      }}
                      placeholder="Term..."
                    />
                    <input
                      style={{ ...styles.input, width: '120px' }}
                      value={kt.verses.join(', ')}
                      onChange={e => {
                        const verses = e.target.value.split(',').map(v => parseInt(v.trim())).filter(Boolean)
                        const updated = [...chart.keyTerms]
                        updated[idx] = { ...updated[idx], verses }
                        setChart(prev => ({ ...prev, keyTerms: updated }))
                      }}
                      placeholder="vv. 1, 4, 16"
                    />
                    <button
                      style={styles.removeBtn}
                      onClick={() => {
                        const updated = chart.keyTerms.filter((_, i) => i !== idx)
                        setChart(prev => ({ ...prev, keyTerms: updated }))
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <textarea
                    style={{ ...styles.textarea, marginTop: '8px' }}
                    value={kt.definition}
                    onChange={e => {
                      const updated = [...chart.keyTerms]
                      updated[idx] = { ...updated[idx], definition: e.target.value }
                      setChart(prev => ({ ...prev, keyTerms: updated }))
                    }}
                    placeholder="Definition or usage note..."
                    rows={2}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: ARCS ── */}
      {activeTab === 'arcs' && (
        <div style={styles.content}>
          <div style={styles.termsHeader}>
            <div>
              <h3 style={styles.tabSectionTitle}>Arcs</h3>
              <p style={{ ...styles.fieldHint, marginTop: '4px' }}>
                Arcs group paragraphs into larger structural movements. Each spans a verse range and appears as a bracket on the left of the printed chart.
              </p>
            </div>
            <button
              style={styles.addBtn}
              onClick={() => {
                const lastVerse = chart.paragraphs.length > 0
                  ? chart.paragraphs[chart.paragraphs.length - 1].endVerse
                  : 1
                const firstVerse = chart.paragraphs.length > 0
                  ? chart.paragraphs[0].startVerse
                  : 1
                setChart(prev => ({
                  ...prev,
                  arcs: [...prev.arcs, {
                    label: `Arc ${prev.arcs.length + 1}`,
                    startVerse: firstVerse,
                    endVerse: lastVerse,
                    accent: prev.arcs.length % 2 === 0 ? 'primary' : 'secondary',
                  }]
                }))
              }}
            >
              + Add Arc
            </button>
          </div>

          {chart.paragraphs.length === 0 ? (
            <p style={styles.emptyText}>Load a chapter first before adding arcs.</p>
          ) : chart.arcs.length === 0 ? (
            <p style={styles.emptyText}>No arcs yet. Click "Add Arc" to group paragraphs into structural movements.</p>
          ) : (
            <div style={styles.termsList}>
              {chart.arcs.map((arc, idx) => {
                const allVerseNums = chart.paragraphs.flatMap(p =>
                  p.verses.map(v => v.number)
                ).filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b)

                return (
                  <div key={idx} style={{
                    ...styles.termCard,
                    borderLeftColor: arc.accent === 'secondary' ? '#2563eb' : '#d24a2a',
                  }}>
                    <div style={styles.termRow}>
                      {/* Accent toggle */}
                      <div style={styles.accentToggle}>
                        <button
                          style={{ ...styles.accentBtn, ...(arc.accent === 'primary' ? styles.accentBtnActive : {}) }}
                          onClick={() => {
                            const updated = [...chart.arcs]
                            updated[idx] = { ...updated[idx], accent: 'primary' }
                            setChart(prev => ({ ...prev, arcs: updated }))
                          }}
                          title="Primary (red bracket)"
                        >●</button>
                        <button
                          style={{ ...styles.accentBtn, ...(arc.accent === 'secondary' ? styles.accentBtnActiveBlue : {}) }}
                          onClick={() => {
                            const updated = [...chart.arcs]
                            updated[idx] = { ...updated[idx], accent: 'secondary' }
                            setChart(prev => ({ ...prev, arcs: updated }))
                          }}
                          title="Secondary (blue bracket)"
                        >●</button>
                      </div>

                      {/* Label */}
                      <input
                        style={{ ...styles.input, flex: 1 }}
                        value={arc.label}
                        onChange={e => {
                          const updated = [...chart.arcs]
                          updated[idx] = { ...updated[idx], label: e.target.value }
                          setChart(prev => ({ ...prev, arcs: updated }))
                        }}
                        placeholder="Arc label..."
                      />

                      {/* Verse range */}
                      <select
                        style={{ ...styles.splitSelect, width: '80px' }}
                        value={arc.startVerse}
                        onChange={e => {
                          const updated = [...chart.arcs]
                          updated[idx] = { ...updated[idx], startVerse: parseInt(e.target.value) }
                          setChart(prev => ({ ...prev, arcs: updated }))
                        }}
                      >
                        {allVerseNums.map(v => <option key={v} value={v}>v.{v}</option>)}
                      </select>
                      <span style={{ color: '#888', fontSize: '13px' }}>–</span>
                      <select
                        style={{ ...styles.splitSelect, width: '80px' }}
                        value={arc.endVerse}
                        onChange={e => {
                          const updated = [...chart.arcs]
                          updated[idx] = { ...updated[idx], endVerse: parseInt(e.target.value) }
                          setChart(prev => ({ ...prev, arcs: updated }))
                        }}
                      >
                        {allVerseNums.map(v => <option key={v} value={v}>v.{v}</option>)}
                      </select>

                      <button
                        style={styles.removeBtn}
                        onClick={() => setChart(prev => ({
                          ...prev,
                          arcs: prev.arcs.filter((_, i) => i !== idx)
                        }))}
                      >×</button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: INTERPRETIVE NOTES ── */}
      {activeTab === 'notes' && (
        <div style={styles.content}>
          <h3 style={styles.tabSectionTitle}>Interpretive Notes</h3>
          <p style={styles.fieldHint} >
            Overall interpretive analysis — themes, arguments, cross-references, applications.
          </p>
          <textarea
            style={{ ...styles.textarea, minHeight: '400px', fontSize: '14px', lineHeight: '1.6' }}
            value={chart.interpretiveNotes}
            onChange={e => setChart(prev => ({ ...prev, interpretiveNotes: e.target.value }))}
            placeholder="Write your interpretive analysis here..."
          />
        </div>
      )}

      {/* ── PRINT PREVIEW OVERLAY ── */}
      {showPrint && (
        <div style={styles.printOverlay}>
          <nav className="no-print" style={styles.printNav}>
            <button style={styles.backBtn} onClick={() => setShowPrint(false)}>
              ← Back to Editor
            </button>
            <span style={styles.navTitle}>
              Print Preview — {chart.book} {chart.chapter}
            </span>
            <button style={styles.saveBtn} onClick={() => window.print()}>
              🖨 Print
            </button>
          </nav>
          <ChapterChart data={chartDataForPrint()} />
        </div>
      )}
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#f8fafc',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    background: '#1e3a5f',
    color: 'white',
  },
  backBtn: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.3)',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  navTitle: {
    fontSize: '16px',
    fontWeight: '600',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  publicLabel: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.8)',
    cursor: 'pointer',
  },
  saveBtn: {
    padding: '7px 18px',
    background: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  printPreviewBtn: {
    padding: '7px 14px',
    background: 'rgba(255,255,255,0.15)',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  printOverlay: {
    position: 'fixed' as const,
    inset: 0,
    background: '#d1d5db',
    zIndex: 1000,
    overflowY: 'auto' as const,
  },
  printNav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    background: '#1e3a5f',
    color: 'white',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1001,
  },
  spinnerWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px 24px',
    background: '#f8fafc',
  },
  spinnerCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '12px',
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '40px 60px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
  },
  spinnerRing: {
    fontSize: '36px',
    color: '#1e3a5f',
    lineHeight: 1,
    fontFamily: 'monospace',
  },
  spinnerText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e3a5f',
  },
  spinnerSub: {
    fontSize: '13px',
    color: '#4338ca',
    fontWeight: '500',
  },
  spinnerHint: {
    fontSize: '12px',
    color: '#aaa',
  },
  loaderWrap: {
    borderBottom: '1px solid #bfdbfe',
  },
  loaderBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 24px',
    background: '#eff6ff',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  modeToggle: {
    display: 'flex',
    border: '1px solid #93c5fd',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  modeBtn: {
    padding: '7px 14px',
    background: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#1e3a5f',
    fontWeight: '500',
  },
  modeBtnActive: {
    background: '#1e3a5f',
    color: 'white',
    fontWeight: '600',
  },
  codexBar: {
    display: 'flex',
    gap: '16px',
    padding: '10px 24px',
    background: '#f0f4ff',
    alignItems: 'center',
    borderTop: '1px solid #c7d7fd',
  },
  codexBarLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#3730a3',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    flexShrink: 0,
  },
  codexField: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  codexFieldLabel: {
    fontSize: '12px',
    color: '#4338ca',
    fontWeight: '600',
    whiteSpace: 'nowrap' as const,
  },
  codexSelect: {
    padding: '5px 8px',
    border: '1px solid #a5b4fc',
    borderRadius: '5px',
    fontSize: '13px',
    background: 'white',
    color: '#1e1b4b',
    cursor: 'pointer',
  },
  codexBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '8px 24px',
    background: '#eef2ff',
    borderBottom: '1px solid #c7d7fd',
  },
  codexBannerTitle: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#3730a3',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    flexShrink: 0,
  },
  codexBannerItems: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap' as const,
  },
  codexBannerItem: {
    fontSize: '12px',
    color: '#4338ca',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  codexCheck: {
    color: '#16a34a',
    fontWeight: '700',
  },
  loaderInput: {
    padding: '8px 12px',
    border: '1px solid #93c5fd',
    borderRadius: '6px',
    fontSize: '14px',
    width: '160px',
  },
  loaderSelect: {
    padding: '8px 10px',
    border: '1px solid #93c5fd',
    borderRadius: '6px',
    fontSize: '14px',
    width: '180px',
    background: 'white',
    cursor: 'pointer',
  },
  loadBtn: {
    padding: '8px 16px',
    background: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  exampleBtn: {
    padding: '8px 16px',
    background: 'white',
    color: '#1e3a5f',
    border: '1px solid #93c5fd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  tabs: {
    display: 'flex',
    gap: '2px',
    padding: '0 24px',
    background: 'white',
    borderBottom: '1px solid #e2e8f0',
  },
  tab: {
    padding: '12px 20px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#666',
    borderBottom: '2px solid transparent',
  },
  tabActive: {
    color: '#1e3a5f',
    borderBottom: '2px solid #1e3a5f',
    fontWeight: '600',
  },
  content: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '24px',
  },
  headerSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '16px',
    background: 'white',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '20px',
    border: '1px solid #e2e8f0',
  },
  headerField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#1e3a5f',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  fieldHint: {
    fontSize: '11px',
    color: '#aaa',
    marginBottom: '4px',
  },
  textarea: {
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    lineHeight: '1.4',
  },
  input: {
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  noParagraphs: {
    textAlign: 'center',
    padding: '60px',
    background: 'white',
    borderRadius: '10px',
    color: '#888',
    border: '1px solid #e2e8f0',
  },
  paragraphList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  paraCard: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderLeft: '4px solid #1e3a5f',
    borderRadius: '8px',
    padding: '16px',
  },
  paraHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
    flexWrap: 'wrap' as const,
  },
  paraBadge: {
    background: '#1e3a5f',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: 0,
  },
  paraTitleInput: {
    flex: 1,
    padding: '4px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#1e3a5f',
    minWidth: '120px',
  },
  paraVerses: {
    fontSize: '12px',
    color: '#888',
    flexShrink: 0,
  },
  splitSelect: {
    padding: '4px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#555',
  },
  versesText: {
    fontSize: '12px',
    lineHeight: '1.7',
    color: '#444',
    fontFamily: 'Georgia, serif',
    padding: '10px',
    background: '#fafafa',
    borderRadius: '6px',
    marginBottom: '12px',
    maxHeight: '180px',
    overflowY: 'auto',
  },
  verseNum: {
    fontSize: '9px',
    fontWeight: '700',
    color: '#1e3a5f',
    marginRight: '2px',
  },
  analysisGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '12px',
  },
  termsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  tabSectionTitle: {
    margin: 0,
    fontSize: '18px',
    color: '#1e3a5f',
  },
  addBtn: {
    padding: '8px 16px',
    background: '#1e3a5f',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  termsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  termCard: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderLeft: '3px solid #2563eb',
    borderRadius: '8px',
    padding: '14px',
  },
  termRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  accentToggle: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    flexShrink: 0,
  },
  accentBtn: {
    width: '20px',
    height: '20px',
    border: '1px solid #ddd',
    borderRadius: '50%',
    background: 'white',
    cursor: 'pointer',
    fontSize: '10px',
    color: '#ccc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  accentBtnActive: {
    background: '#fef2f0',
    borderColor: '#d24a2a',
    color: '#d24a2a',
  },
  accentBtnActiveBlue: {
    background: '#eff6ff',
    borderColor: '#2563eb',
    color: '#2563eb',
  },
  removeBtn: {
    padding: '4px 10px',
    background: 'white',
    border: '1px solid #fca5a5',
    color: '#dc2626',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: 1,
  },
  emptyText: {
    textAlign: 'center',
    color: '#888',
    padding: '40px',
  },
}
